import useGetRequest from 'hooks/useGetRequest';
import React, { useContext, useState } from 'react';
import { LearnerActivity, LearnerCourse, LearnerLearningObjective, LearnerModule } from 'models/LearnerCourse';
import { useParams } from 'react-router-dom';
import { Course } from 'models/Course';
import { LocationType } from 'models/ActivityMeeting';
import { LearnerQuestion } from 'models/LearnerQuestionnaire';
import { AuthContext } from './AuthContext';

type LearnerCourseStep = {
    id: string;
    stepType: 'Course' | 'Module' | 'Activity';
};

type LearnerCourseContextValue = {
    learnerCourse: LearnerCourse | undefined;
    setCourseState: React.Dispatch<React.SetStateAction<LearnerCourse | undefined>>;
    refetchLearnerCourse: () => Promise<void>;
    errors: boolean;
    step: LearnerCourseStep;
    setStep: React.Dispatch<React.SetStateAction<LearnerCourseStep>>;
    previewMode: boolean;
    isFinalActivity: boolean;
    immersiveMode: boolean;
    setImmersiveMode: React.Dispatch<React.SetStateAction<boolean>>;
};

export const LearnerCourseContext = React.createContext<LearnerCourseContextValue>({
    learnerCourse: undefined,
    refetchLearnerCourse: async () => {},
    setCourseState: () => {},
    errors: false,
    step: { id: '', stepType: 'Course' },
    setStep: () => {},
    previewMode: false,
    isFinalActivity: false,
    immersiveMode: false,
    setImmersiveMode: () => {},
});

type LearnerCourseProvierProps = {
    children: React.ReactNode;
    previewMode: boolean;
};

export function LearnerCourseProvider({ children, previewMode }: LearnerCourseProvierProps): JSX.Element {
    const { userData } = useContext(AuthContext);
    const [course, setCourseState] = useState<LearnerCourse>();
    const [step, setStep] = useState<LearnerCourseStep>({ id: '', stepType: 'Course' });
    const [immersiveMode, setImmersiveMode] = useState(false);

    const params = useParams();
    const { enrollmentId } = params;
    const { courseId } = params;

    const {
        data: learnerCourse,
        refetchData: refetchLearnerCourse,
        errors: learnerCourseErrors,
    } = useGetRequest<LearnerCourse>(`learner/courses/${enrollmentId}`, previewMode);

    const { data: courseData } = useGetRequest<Course>(`courses/${courseId}`, !previewMode);

    React.useEffect(() => {
        if (learnerCourse) {
            setCourseState(learnerCourse);
        }
    }, [learnerCourse]);

    React.useEffect(() => {
        if (courseData) {
            const now = new Date();
            const organisation = userData.organisation!;

            const learnerCourse: LearnerCourse = {
                enrollmentId: courseData.id,
                startDate: now.toDateString(),
                endDate: now.toDateString(),
                title: courseData.title,
                description: courseData.description,
                learningObjectives: [],
                modules: [],
                cancelledDate: null,
                completedDate: null,
                trainerInfo: {
                    firstName: userData.firstName,
                    lastName: userData.lastName,
                    email: userData.email,
                    organisationName: organisation.name,
                    organisationPublicName: organisation.publicName,
                    organisationLogo: organisation.logoS3Filepath,
                    bio: 'Preview bio for your Trainer',
                    profileImagePath: '',
                },
            };

            learnerCourse.learningObjectives = courseData.learningObjectives.map((o) => {
                const learningObjective: LearnerLearningObjective = {
                    id: o.id,
                    order: o.order,
                    description: o.description,
                };
                return learningObjective;
            });

            learnerCourse.modules = courseData.modules.map((m) => {
                const module: LearnerModule = {
                    id: m.id,
                    title: m.title,
                    description: m.description,
                    order: m.order,
                    dateCreated: new Date(m.dateCreated),
                    moduleType: m.moduleType,
                    activities: m.activities.map((a) => {
                        const locationType = LocationType.ONLINE;

                        const activity: LearnerActivity = {
                            activityStatusId: a.id,
                            title: a.title,
                            description: a.description,
                            order: a.order,
                            type: a.type,
                            locationType: locationType,
                            startDate: now,
                            completed: false,
                            questionsTotal: a.questions.length,
                            questionsAnswered: 0,
                            durationInMins: a.durationInMins,
                            moduleId: m.id,
                            questions: a.questions.map((q) => {
                                const question: LearnerQuestion = {
                                    id: q.id,
                                    question: q.question,
                                    hint: q.hint,
                                    order: q.order,
                                    answer: '',
                                };
                                return question;
                            }),
                            content: a.content,
                            videoDocument: a.videoDocument,
                            imageDocuments: a.imageDocuments,
                            audioDocument: a.audioDocument,
                            resources: a.resources,
                            buildMethod: a.buildMethod,
                            taskContents: a.taskContents
                                .sort((a, b) => {
                                    return a.order - b.order;
                                })
                                .map((x) => {
                                    if (x.type === 'text') {
                                        return {
                                            type: 'text',
                                            order: x.order,
                                            text: x.text,
                                            audio: x.audio?.s3Filepath ?? '',
                                        };
                                    }
                                    return {
                                        type: 'image',
                                        order: x.order,
                                        image: x.image.s3Filepath,
                                        imageDescription: x.imageDescription,
                                        audio: x.audio?.s3Filepath ?? '',
                                    };
                                }),
                        };
                        return activity;
                    }),
                };
                return module;
            });

            setCourseState(learnerCourse);
        }
    }, [courseData]); //eslint-disable-line

    let errors: boolean = false;
    if (learnerCourseErrors && learnerCourseErrors.length) {
        errors = true;
    }

    const isFinalActivity = React.useMemo(() => {
        if (!course) return false;
        const activities = course.modules.flatMap((module) => module.activities);
        const completedActivities = activities.filter((activity) => activity.completed);
        return completedActivities.length === activities.length - 1;
    }, [course]);

    return (
        <LearnerCourseContext.Provider
            value={{
                learnerCourse: course,
                setCourseState: setCourseState,
                refetchLearnerCourse: refetchLearnerCourse,
                errors: errors,
                step: step,
                setStep: setStep,
                previewMode: previewMode,
                isFinalActivity: isFinalActivity,
                immersiveMode,
                setImmersiveMode,
            }}
        >
            {children}
        </LearnerCourseContext.Provider>
    );
}
